import { useState } from 'react';
import { t, huidigeTaal } from '../i18n.js';
import { Ernst } from '../components/Status.jsx';
import { hoofdstukkenVoor, ALERTS } from '../uitleg.js';
import { versiesVoor, VERSIES } from '../versies.js';

/**
 * De handleiding en *Wat is er nieuw* (docs/besluiten/versies-en-uitleg.md), voor medewerkers en voor klanten: een klant
 * ziet alleen wat voor klanten is. De tekst staat in uitleg.js en versies.js, in beide talen.
 */
const taal = (x) => x?.[huidigeTaal()] ?? x?.nl ?? '';
const datum = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString(huidigeTaal() === 'en' ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

/** De modules in de volgorde van de catalogus (src/core/modules.js); de namen zijn sleutels in i18n.js. */
const MODULES = [['basis', 'ADTS ZERO Basis'], ['vpn', 'VPN-bewaking'], ['firewall', 'Firewall-hygiëne'], ['kwetsbaar', 'Kwetsbare versies'],
  ['scan', 'Extern scannen'], ['umbrella', 'DNS Security'], ['secureendpoint', 'Secure Endpoint'], ['identity', 'Identity'], ['duo', 'Secure Connect'], ['mail', 'Email Security'], ['blokkeren', 'Blokkeren']];

export default function Uitleg({ klant = false, versie }) {
  return (
    <>
      <div className="page-head"><div>
        <h1>{t('Uitleg')}</h1>
        <p className="sub">{t('Wat ADTS ZERO doet, wat elk alert betekent en wat je ermee doet.')}{versie?.number && <> · ADTS ZERO {versie.number}</>}</p>
      </div></div>
      <div className="uitleg">
        {hoofdstukkenVoor(klant).map((h) => (
          <section key={h.id} className="card" id={h.id}>
            <header><h2>{taal(h.titel)}</h2></header>
            {h.alineas?.map((a, i) => <p key={i}>{taal(a)}</p>)}
            {h.lijst && <dl className="uitleglijst">{h.lijst.map((x, i) => <div key={i}><dt>{taal(x.term)}</dt><dd>{taal(x.tekst)}</dd></div>)}</dl>}
          </section>
        ))}
        <section className="card" id="alerts">
          <header><h2>{t('Alle alerts, per module')}</h2></header>
          {MODULES.map(([id, naam]) => (
            <div key={id} className="uitlegmodule">
              <h3>{t(naam)}</h3>
              <table>
                <thead><tr><th>{t('Alert')}</th><th>{t('Ernst')}</th><th>{t('Wanneer')}</th><th>{t('Wat te doen')}</th></tr></thead>
                <tbody>{ALERTS.filter((a) => a.module === id).map((a) => (
                  <tr key={a.rule}><td><b>{taal(a.naam)}</b></td><td><Ernst ernst={a.ernst} /></td><td>{taal(a.wanneer)}</td><td>{taal(a.doen)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}

/**
 * *Wat is er nieuw*, met de twee sporen (versies.js): een klant in het klantportaal ziet alleen de hoofdlijnen. Een
 * medewerker ziet standaard alles (intern) en kan met de knop het klantspoor bekijken, precies zoals de klant het ziet;
 * bij elke versie staat dan of een klant hem ook ziet.
 */
export function Nieuw({ klant = false, versie }) {
  const [spoor, zetSpoor] = useState('intern');
  const alsKlant = klant || spoor === 'klant';
  return (
    <>
      <div className="page-head"><div>
        <h1>{t('Wat is er nieuw')}</h1>
        <p className="sub">{t('Je gebruikt ADTS ZERO {versie}', { versie: versie?.number ?? VERSIES[0].versie })}{!klant && versie?.build && <> · <span className="mono">{versie.build}</span></>}</p>
      </div>
        {!klant && (
          <div className="tabs" role="tablist" aria-label={t('Welk spoor')} style={{ margin: 0, border: 0 }}>
            <button type="button" role="tab" aria-selected={spoor === 'intern'} className={spoor === 'intern' ? 'aan' : undefined} onClick={() => zetSpoor('intern')}>{t('Intern ADTS (alles)')}</button>
            <button type="button" role="tab" aria-selected={spoor === 'klant'} className={spoor === 'klant' ? 'aan' : undefined} onClick={() => zetSpoor('klant')}>{t('Voor klanten (hoofdlijnen)')}</button>
          </div>
        )}
      </div>
      {!klant && spoor === 'klant' && <p className="muted" style={{ marginTop: -8, marginBottom: 14 }}>{t('Dit is precies wat een klant in het klantportaal ziet.')}</p>}
      {versiesVoor(alsKlant).map((v, i) => (
        <section key={v.versie} className="card" style={{ marginBottom: 14 }}>
          <header><h2>ADTS ZERO {v.versie}{i === 0 && <span className="pill" style={{ marginLeft: 8 }}>{t('nieuwste')}</span>}{!klant && spoor === 'intern' && v.klant && <span className="pill" style={{ marginLeft: 8 }}>{t('ook voor klanten')}</span>}</h2>
            <span className="meta">{datum(v.datum)}</span></header>
          <ul className="nieuwlijst">{v.items.map((x, j) => <li key={j}>{taal(x)}</li>)}</ul>
        </section>
      ))}
    </>
  );
}
